import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

const CONSENT_KEY = 'fms.analyticsConsent';

export type ConsentState = 'granted' | 'denied' | 'unset';

export function getAnalyticsConsent(): ConsentState {
  const value = window.localStorage.getItem(CONSENT_KEY);
  return value === 'granted' || value === 'denied' ? value : 'unset';
}

/**
 * Analytics (GA4/GTM/Clarity) load ONLY after explicit consent — nothing is
 * injected before the citizen decides, and "No thanks" is remembered too.
 */
export function ConsentBanner({
  labels,
  onDecision,
}: {
  labels: { message: string; accept: string; decline: string };
  onDecision?: (state: ConsentState) => void;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(getAnalyticsConsent() === 'unset');
  }, []);
  if (!visible) return null;

  const decide = (state: Exclude<ConsentState, 'unset'>) => {
    window.localStorage.setItem(CONSENT_KEY, state);
    setVisible(false);
    onDecision?.(state);
  };

  return (
    <Paper
      role="region"
      aria-label="Cookie and analytics consent"
      elevation={8}
      sx={{
        position: 'fixed',
        insetInlineStart: 16,
        insetInlineEnd: 16,
        bottom: 16,
        zIndex: (theme) => theme.zIndex.snackbar,
        p: 2,
        maxWidth: 560,
        marginInline: 'auto',
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
        <Typography variant="body2" sx={{ flex: 1 }}>
          {labels.message}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" onClick={() => decide('denied')}>
            {labels.decline}
          </Button>
          <Button variant="contained" size="small" onClick={() => decide('granted')}>
            {labels.accept}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
